/* Decorative bird: responsive perches and a constant travel speed in pixels/second. */
(() => {
  const scenes = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sync = entry => {
    const paused = !entry.visible || document.hidden || document.body.dataset.motionPaused === 'true' || motion.matches;
    entry.animations.forEach(animation => paused ? animation.pause() : animation.play());
  };
  function prepare(root = document) {
    root.querySelectorAll('.hero-landscape,.footer-landscape').forEach(scene => {
      if (scenes.has(scene)) return;
      const bird = scene.querySelector('.bird-flight');
      const image = scene.querySelector(':scope > img');
      const perch = bird?.querySelector('.bird-perch');
      if (!bird || !image || !perch || !bird.animate) return;
      const footer = scene.classList.contains('footer-landscape');
      const entry = { animations: [], visible: false, duration: 1 };
      const update = () => {
        if (!scene.isConnected) return;
        const progress = entry.animations.length ? (Number(entry.animations[0].currentTime) % entry.duration) / entry.duration : 0;
        entry.animations.forEach(animation => animation.cancel());
        entry.animations = [];
        delete bird.dataset.journey;
        if (motion.matches || document.body.dataset.motion === 'false') return;
        const expected = footer ? 'landscape-footer' : 'landscape-hero';
        if (!(image.currentSrc || image.src).includes(expected) || !image.naturalWidth) return;
        const bounds = scene.getBoundingClientRect();
        const box = image.getBoundingClientRect();
        let width = box.width, height = box.height, offsetX = 0, offsetY = 0;
        if (getComputedStyle(image).objectFit === 'cover') {
          const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
          const renderedWidth = image.naturalWidth * scale;
          const renderedHeight = image.naturalHeight * scale;
          offsetX = (width - renderedWidth) * .5;
          offsetY = height - renderedHeight;
          width = renderedWidth; height = renderedHeight;
        }
        // Feet sit on the case surface, or the bridge's upper railing.
        const x = box.left - bounds.left + offsetX + width * (footer ? .475 : .25);
        const y = box.top - bounds.top + offsetY + height * (footer ? .704 : .699);
        const size = bird.offsetWidth;
        if (x < size * .4 || x > bounds.width - size * .4 || y < 16 || y > bounds.height - 4) return;
        const ax = x - size * .6, ay = y - size * .72;
        const start = [-80, Math.max(-10, ay - 70)];
        const approach = [ax - 54, ay - 25];
        const departure = [ax + 65, Math.max(-10, ay - 40)];
        const end = [bounds.width + 80, -8];
        // Every flight leg uses the same 70 px/s; only the short landing eases out.
        const speed = 70;
        const flightIn = Math.hypot(approach[0] - start[0], approach[1] - start[1]) / speed;
        const landing = Math.hypot(ax - approach[0], ay - approach[1]) / speed;
        const rest = 4.6;
        const takeoff = Math.hypot(departure[0] - ax, departure[1] - ay) / speed;
        // Sample a broad S-curve and time it by travelled distance, keeping 70 px/s.
        const amplitude = Math.min(58, Math.max(24, (departure[1] + 24) * .7));
        const route = [];
        let routeLength = 0;
        for (let i = 0; i <= 80; i++) {
          const u = i / 80;
          const px = departure[0] + (end[0] - departure[0]) * u;
          const py = departure[1] + (end[1] - departure[1]) * u
            - amplitude * Math.sin(2 * Math.PI * u) * Math.sin(Math.PI * u);
          if (i) routeLength += Math.hypot(px - route[i - 1].x, py - route[i - 1].y);
          route.push({ x: px, y: py, distance: routeLength });
        }
        const flightOut = routeLength / speed;
        const total = flightIn + landing + rest + takeoff + flightOut + .6;
        const landAt = (flightIn + landing) / total;
        const liftAt = (flightIn + landing + rest) / total;
        const transform = (px, py, degrees = 0) => `translate(${px}px,${py}px) rotate(${degrees}deg)`;
        const timing = { duration: total * 1000, iterations: Infinity, easing: 'linear' };
        const pose = (seconds, position, opacity = 1) => ({ offset: seconds / total, backgroundPosition: position, opacity, easing: 'steps(1,end)' });
        bird.dataset.journey = footer ? 'bridge' : 'case';
        bird.style.setProperty('--land-x', `${ax}px`);
        bird.style.setProperty('--land-y', `${ay}px`);
        bird.style.setProperty('--bird-rest-at', String(landAt));
        bird.style.setProperty('--bird-lift-at', String(liftAt));
        entry.animations = [
          bird.animate([
            { offset: 0, transform: transform(...start, -3) },
            { offset: flightIn / total, transform: transform(...approach, -5), easing: 'cubic-bezier(.2,.1,.7,1)' },
            { offset: landAt, transform: transform(ax, ay) },
            { offset: liftAt, transform: transform(ax, ay) },
            { offset: liftAt + takeoff / total, transform: transform(...departure, -8) },
            ...route.slice(1).map((point, index) => {
              const previous = route[index];
              const pitch = Math.max(-14, Math.min(14, Math.atan2(point.y - previous.y, point.x - previous.x) * 180 / Math.PI));
              const bank = -8 + (pitch + 8) * Math.min(1, (index + 1) / 10);
              return {
                offset: liftAt + (takeoff + point.distance / speed) / total,
                transform: transform(point.x, point.y, bank)
              };
            }),
            { offset: 1, transform: transform(...end, Math.max(-14, Math.min(14, Math.atan2(end[1] - route[79].y, end[0] - route[79].x) * 180 / Math.PI))) }
          ], timing),
          bird.querySelector('.bird-wings').animate([
            { offset: 0, opacity: 1, easing: 'steps(1,end)' },
            { offset: (flightIn + landing - .4) / total, opacity: 0, easing: 'steps(1,end)' },
            { offset: liftAt + .22 / total, opacity: 1, easing: 'steps(1,end)' },
            { offset: 1, opacity: 1 }
          ], timing),
          perch.animate([
            pose(0, '0% 0%', 0),
            pose(flightIn + landing - .4, '0% 0%'),
            pose(flightIn + landing, '50% 0%'),
            pose(flightIn + landing + .22, '100% 0%'),
            pose(flightIn + landing + 1.3, '0% 100%'),
            pose(flightIn + landing + 2.1, '50% 100%'),
            pose(flightIn + landing + 3.1, '0% 100%'),
            pose(flightIn + landing + 3.8, '100% 0%'),
            pose(flightIn + landing + rest, '100% 100%'),
            pose(flightIn + landing + rest + .22, '100% 100%', 0),
            pose(total, '100% 100%', 0)
          ], timing)
        ];
        entry.duration = timing.duration;
        entry.animations.forEach(animation => { animation.currentTime = progress * timing.duration; });
        sync(entry);
      };
      entry.update = update;
      entry.image = image;
      entry.resize = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
      entry.resize?.observe(scene); entry.resize?.observe(image);
      entry.visibility = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        entry.visible = entries[0].isIntersecting; sync(entry);
      }, { rootMargin: '60px' }) : null;
      entry.visibility?.observe(scene);
      if (!entry.visibility) entry.visible = true;
      image.addEventListener('load', update);
      if (!entry.resize) window.addEventListener('resize', update);
      motion.addEventListener('change', update);
      scenes.set(scene, entry);
      update();
    });
  }
  prepare();
  new MutationObserver(() => scenes.forEach(sync)).observe(document.body, { attributes: true, attributeFilter: ['data-motion-paused'] });
  document.addEventListener('visibilitychange', () => scenes.forEach(sync));
  document.addEventListener('shopify:section:load', event => prepare(event.target));
  document.addEventListener('shopify:section:unload', event => {
    scenes.forEach((entry, scene) => {
      if (!event.target.contains(scene)) return;
      entry.animations.forEach(animation => animation.cancel());
      entry.resize?.disconnect(); entry.visibility?.disconnect();
      entry.image.removeEventListener('load', entry.update);
      window.removeEventListener('resize', entry.update);
      motion.removeEventListener('change', entry.update);
      scenes.delete(scene);
    });
  });
})();

